import { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { assetsAPI, assetsReportsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/Table';
import { ArrowLeft } from 'lucide-react';
import {
  VAT_RECLAIM_STATUSES,
  labelFor,
  formatAssetDate,
  formatAssetCurrency,
} from '../../../lib/assetConstants';

const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);

export default function VatPreRegistrationReport() {
  const navigate = useNavigate();
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);

  const { data, isLoading, error, refetch } = useQuery(
    ['assets-reports', 'vat-pre-registration', includeExcluded],
    () => assetsReportsAPI.getVatPreRegistration({ includeExcluded }),
    { retry: false }
  );

  const handleReview = async (assetId, status) => {
    try {
      setReviewingId(assetId);
      await assetsAPI.updateVatReclaimReview(assetId, { vatReclaimStatus: status });
      await refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setReviewingId(null);
    }
  };

  const missingDate = error?.response?.data?.code === 'VAT_REGISTRATION_DATE_MISSING';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <Button variant="ghost" onClick={() => navigate('/tenant/assets/reports')} className="mb-4 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Reports
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              VAT pre-registration reclaim
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Advisory report — confirm eligibility with your accountant before claiming.
            </p>
          </div>
          {data?.vatRegistrationDate && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              VAT registration: <strong className="text-gray-900 dark:text-gray-100">{formatAssetDate(data.vatRegistrationDate)}</strong>
            </div>
          )}
        </div>

        {missingDate && (
          <Card className="mb-6 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
            <CardContent className="pt-6">
              <p className="text-sm text-amber-900 dark:text-amber-200">
                Set your VAT registration date in{' '}
                <Link to="/tenant/assets/settings" className="underline font-medium">
                  Assets settings
                </Link>{' '}
                to run this report.
              </p>
            </CardContent>
          </Card>
        )}

        {data?.disclaimer && (
          <Card className="mb-6 bg-white dark:bg-gray-800">
            <CardContent className="pt-6">
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{data.disclaimer}</p>
            </CardContent>
          </Card>
        )}

        <div className="mb-4 flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeExcluded}
              onChange={(e) => setIncludeExcluded(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Include excluded assets
          </label>
        </div>

        {isLoading && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading report…</div>
        )}

        {error && !missingDate && (
          <div className="text-red-600 dark:text-red-400 text-sm">
            {error.response?.data?.error || error.message || 'Failed to load report'}
          </div>
        )}

        {data && (
          <>
            <Card className="mb-6 bg-white dark:bg-gray-800">
              <CardHeader>
                <CardTitle className="text-gray-900 dark:text-gray-100">Summary</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Assets</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{data.summary?.assetCount ?? 0}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Stock lines</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{data.summary?.stockCount ?? 0}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 dark:text-gray-400">Total potential VAT reclaim (assets)</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {currency(data.summary?.totalPotentialVatReclaim)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6 bg-white dark:bg-gray-800 overflow-hidden">
              <CardHeader>
                <CardTitle className="text-gray-900 dark:text-gray-100">Assets</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ref</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Purchase date</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">VAT</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Review</TableHead>
                      <TableHead>Reviewed</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.assets || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-gray-500 dark:text-gray-400 py-8">
                          No matching assets in the 4-year pre-registration window.
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.assets.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Link to={`/tenant/assets/${row.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                              {row.assetRef}
                            </Link>
                          </TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{formatAssetDate(row.purchaseDate)}</TableCell>
                          <TableCell>{row.supplier || '—'}</TableCell>
                          <TableCell className="text-right">{formatAssetCurrency(row.purchaseVatAmount)}</TableCell>
                          <TableCell>{labelFor(VAT_RECLAIM_STATUSES, row.vatReclaimStatus)}</TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              row.eligibleIndicator === 'likely_eligible'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : row.eligibleIndicator === 'excluded'
                                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                            }`}>
                              {row.eligibleIndicator?.replace(/_/g, ' ')}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-gray-600 dark:text-gray-400">
                            {row.vatReclaimReviewedByName ? (
                              <>
                                {row.vatReclaimReviewedByName}
                                <br />
                                {formatAssetDate(row.vatReclaimReviewedAt)}
                              </>
                            ) : (
                              '—'
                            )}
                            {row.vatReclaimExcludedReason && (
                              <p className="mt-1 text-red-600 dark:text-red-400">{row.vatReclaimExcludedReason}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <select
                              value={row.vatReclaimStatus}
                              disabled={reviewingId === row.id}
                              onChange={(e) => handleReview(row.id, e.target.value)}
                              className={selectClass}
                            >
                              {VAT_RECLAIM_STATUSES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {(data.stock || []).length > 0 && (
              <Card className="bg-white dark:bg-gray-800 overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-gray-900 dark:text-gray-100">Stock (on hand with VAT paid)</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead className="text-right">VAT paid</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.stock.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Link to={`/tenant/stock/${row.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                              {row.name}
                            </Link>
                          </TableCell>
                          <TableCell>{row.sku || '—'}</TableCell>
                          <TableCell>{row.quantityOnHand}</TableCell>
                          <TableCell className="text-right">{formatAssetCurrency(row.vatPaid)}</TableCell>
                          <TableCell>{row.storageLocation || '—'}</TableCell>
                          <TableCell className="text-xs text-gray-500 dark:text-gray-400">{row.vatReclaimNotes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
